// =============================================================================
//  apiPageTemplateOverride.js — client for the Family "System Templates"
//  override feature (server_elakay/elakay_controllers/pageTemplateOverride.js).
//
//  Base URL: elakay_API + /page-template-overrides
//  Auth: Bearer token for the Family-only mutating endpoints; the resolver
//  GET (getPageTemplateOverride) is deliberately public, same convention as
//  getFirstAnimationSettingsAPI -- any page view needs to resolve it, not
//  just Family/signed-in users.
// =============================================================================

import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";

const getToken = () => isAuthenticated()?.token || "";

async function apiFetch(path, { method = "GET", body, token } = {}) {
  const headers = { Accept: "application/json", "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const opts = { method, headers };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch(`${elakay_API}${path}`, opts);
  const data = await res.json();
  if (!res.ok) {
    const error = new Error(data.error || `Erreur ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return data;
}

// Family-only: lightweight metadata for every known override (never the full
// PageBuilder doc content -- see the controller's own §42/§43 comment).
export const listPageTemplateOverrides = (token) =>
  apiFetch("/page-template-overrides", { token: token || getToken() });

// Public: the real runtime resolver. Never throws on "no override" -- returns
// { priority: false, pagebuilderDoc: null } instead, matching the backend's
// own fail-safe contract.
export const getPageTemplateOverride = (templateKey) =>
  apiFetch(`/page-template-overrides/${encodeURIComponent(templateKey)}`);

// Family-only: create-or-update the override pointer for templateKey
// (never a second document -- see the backend's real unique index + upsert).
export const upsertPageTemplateOverride = (templateKey, payload, token) =>
  apiFetch(`/page-template-overrides/${encodeURIComponent(templateKey)}`, {
    method: "PUT",
    body: payload,
    token: token || getToken(),
  });

// Family-only: whether this custom version is eligible to be used AT ALL.
// Deliberately a separate call/field from Priority (never merged) -- OFF
// makes the runtime ignore the override completely, regardless of Priority,
// without touching Priority itself or the linked PageBuilder document (see
// the backend controller's own header comment on setActive).
export const setPageTemplateOverrideActive = (templateKey, active, token) =>
  apiFetch(`/page-template-overrides/${encodeURIComponent(templateKey)}/active`, {
    method: "PATCH",
    body: { active },
    token: token || getToken(),
  });

// Family-only: of the versions that are Active, should the custom one
// actually replace the original? Has no runtime effect unless Active is
// ALSO true (see setPageTemplateOverrideActive above).
export const setPageTemplateOverridePriority = (templateKey, priority, token) =>
  apiFetch(`/page-template-overrides/${encodeURIComponent(templateKey)}/priority`, {
    method: "PATCH",
    body: { priority },
    token: token || getToken(),
  });

// Family-only: which reusable Page Template's DESIGN a Content Page uses
// (`templateKey` must be a "content.*" key). NEVER a data source -- a
// Content Page's data is fixed and unredirectable (see the model/controller's
// own comment on `selectedTemplateKey`). "" clears it back to "no template
// selected, use my own current/original rendering". Never touches this
// Content Page's own legacy pagebuilderDoc/active/priority.
export const setContentPageSelectedTemplate = (templateKey, selectedTemplateKey, token) =>
  apiFetch(`/page-template-overrides/${encodeURIComponent(templateKey)}/selected-template`, {
    method: "PATCH",
    body: { selectedTemplateKey },
    token: token || getToken(),
  });

// ONE central assignment function (fix-400 + system-template-selection
// mission §29/§30: "Ne crée pas une logique pour modal Link et une autre
// différente pour Page Template dropdown"). Every UI entry point that lets
// Family pick a Page/System Template for a Content Page -- the "Templates &
// Sources de données" modal's Select+Lier flow AND the Page Template
// dropdown -- must call THIS, never re-implement the
// "update-if-exists-else-create" branch themselves. `selectedTemplateKey`
// may be a `contentPageTemplate.*` (Family-built) or `contentTemplate.*`
// (System/Default) key, or "" to clear the selection; auto-activation
// (`active:true` the moment a real key is set) happens entirely server-side
// (see setSelectedTemplate/upsertOverride's own comments) -- this function
// never needs to send `active` itself.
//
// `hasExistingOverride` is the caller's own already-known truth (from
// whatever batch-loaded map it already keeps) -- deliberately NOT re-fetched
// here, to avoid a redundant network round trip on every single assignment.
export const assignTemplateToPage = (pageKey, selectedTemplateKey, { hasExistingOverride, group = "content", title, originalRoute, token } = {}) =>
  hasExistingOverride
    ? setContentPageSelectedTemplate(pageKey, selectedTemplateKey, token)
    : upsertPageTemplateOverride(pageKey, { group, title, originalRoute, selectedTemplateKey, createNew: true }, token);

// Family-only: clears ONLY the linked custom pagebuilderDoc (back to "use
// the original source template HTML"); active/priority/selectedTemplateKey/
// group/title are all left untouched, and the underlying PageBuilder
// document itself is never deleted (see the backend controller's own
// comment).
export const resetPageTemplateOverrideHtml = (templateKey, token) =>
  apiFetch(`/page-template-overrides/${encodeURIComponent(templateKey)}/reset-html`, {
    method: "PATCH",
    token: token || getToken(),
  });

// Family-only: removes the override POINTER only -- the linked PageBuilder
// document itself is never deleted. Prefer setPageTemplateOverridePriority(key,
// false) when the goal is just "stop using this version" (keeps it
// recoverable); use this only for an explicit, confirmed "forget this link".
export const resetPageTemplateOverride = (templateKey, token) =>
  apiFetch(`/page-template-overrides/${encodeURIComponent(templateKey)}`, {
    method: "DELETE",
    token: token || getToken(),
  });
